import { can, type Action } from './permissions';
import type { UserRole } from '@/types/user';

/**
 * Identifiers of the authenticated pages
 */
export type RouteId =
  | 'dashboard'
  | 'stats'
  | 'nodes'
  | 'domains'
  | 'origin-groups'
  | 'certificates'
  | 'nginx'
  | 'tasks'
  | 'users'
  | 'audit';

/**
 * Route metadata - single source for the router (App), the sidebar and the
 * header title/breadcrumbs.
 */
export interface AppRoute {
  id: RouteId;
  /** Absolute path ("/" for the dashboard) */
  path: string;
  /** Page title shown in the header and breadcrumbs */
  title: string;
  /** Sidebar label */
  navLabel: string;
  /** Sidebar icon */
  icon: string;
  /** Permission required to see and open the page (omit = any authenticated user) */
  requires?: Action;
}

export const APP_TITLE = 'CDN Manager';

export const APP_ROUTES: readonly AppRoute[] = [
  { id: 'dashboard', path: '/', title: 'Dashboard', navLabel: 'Dashboard', icon: '📊' },
  { id: 'stats', path: '/stats', title: 'Statistics', navLabel: 'Statistics', icon: '📈', requires: 'stats:read' },
  { id: 'nodes', path: '/nodes', title: 'CDN Nodes', navLabel: 'Nodes', icon: '🖥️' },
  { id: 'domains', path: '/domains', title: 'Domains', navLabel: 'Domains', icon: '🌐' },
  {
    id: 'origin-groups',
    path: '/origin-groups',
    title: 'Origin Groups',
    navLabel: 'Origin Groups',
    icon: '🔗',
  },
  { id: 'certificates', path: '/certificates', title: 'Certificates', navLabel: 'Certificates', icon: '🔒' },
  { id: 'nginx', path: '/nginx', title: 'NGINX Configs', navLabel: 'NGINX Configs', icon: '⚙️' },
  { id: 'tasks', path: '/tasks', title: 'Tasks', navLabel: 'Tasks', icon: '📋' },
  { id: 'users', path: '/users', title: 'Users', navLabel: 'Users', icon: '👥', requires: 'users:manage' },
  { id: 'audit', path: '/audit', title: 'Audit Logs', navLabel: 'Audit Logs', icon: '📝', requires: 'audit:read' },
];

/**
 * Path of a route, e.g. "/stats"
 */
export function getRoutePath(id: RouteId): string {
  const route = APP_ROUTES.find((r) => r.id === id);
  if (!route) throw new Error(`Unknown route: ${id}`);
  return route.path;
}

/**
 * Whether a user with `role` may open the route
 */
export function canAccessRoute(route: AppRoute, role: UserRole | null | undefined): boolean {
  return !route.requires || can(role, route.requires);
}

/**
 * Routes visible in the sidebar for a role
 */
export function getNavRoutes(role: UserRole | null | undefined): AppRoute[] {
  return APP_ROUTES.filter((route) => canAccessRoute(route, role));
}

/**
 * Find the route for a pathname: exact match first, then by first segment
 * (so "/nodes/123" resolves to the Nodes route)
 */
export function findRoute(pathname: string): AppRoute | undefined {
  const exact = APP_ROUTES.find((route) => route.path === pathname);
  if (exact) return exact;

  const firstSegment = pathname.split('/').filter(Boolean)[0];
  if (!firstSegment) return undefined;
  return APP_ROUTES.find((route) => route.path === `/${firstSegment}`);
}

/**
 * Header title for a pathname
 */
export function getPageTitle(pathname: string): string {
  return findRoute(pathname)?.title ?? APP_TITLE;
}

export interface Breadcrumb {
  label: string;
  /** Present for every crumb except the current page */
  path?: string;
}

/**
 * Breadcrumbs for a pathname: Home / <route title> / <sub-segments>
 */
export function getBreadcrumbs(pathname: string): Breadcrumb[] {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs: Breadcrumb[] = [{ label: 'Home', path: '/' }];

  let currentPath = '';
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const route = APP_ROUTES.find((r) => r.path === currentPath);
    breadcrumbs.push({
      label: route?.title ?? segment.charAt(0).toUpperCase() + segment.slice(1),
      path: index < segments.length - 1 ? currentPath : undefined,
    });
  });

  return breadcrumbs;
}
